import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import type { InvoiceDetail, InvoicePayment } from '@lib/api/invoices';

// Light-themed, read-only invoice detail for rendering inside a detail panel (not a
// dark modal). Rich actions — PDF/print/attachments — stay in InvoiceDetailModal,
// reachable via "Full view". The mutating actions reuse the page's existing handlers.

interface InvoiceInlineDetailProps {
  invoice: InvoiceDetail;
  onBack: () => void;
  onEdit?: () => void;
  onSend?: () => void;
  onRecordPayment?: () => void;
  onOpenFull?: () => void;
  formatDate: (s?: string) => string;
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(Number(n) || 0);

export const InvoiceInlineDetail = ({
  invoice, onBack, onEdit, onSend, onRecordPayment, onOpenFull, formatDate,
}: InvoiceInlineDetailProps) => {
  const canEdit = !['paid', 'cancelled', 'revised'].includes(invoice.status);
  const canSend = ['draft', 'overdue'].includes(invoice.status);
  const canRecordPayment = !['paid', 'cancelled', 'revised'].includes(invoice.status);

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-primary transition-colors"
        >
          <span className="material-symbols-outlined text-base">chevron_left</span>
          All invoices
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && onEdit && (
            <button onClick={onEdit} className="px-3 py-1.5 text-sm font-medium border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50">Edit</button>
          )}
          {canSend && onSend && (
            <button onClick={onSend} className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Send</button>
          )}
          {canRecordPayment && onRecordPayment && (
            <button onClick={onRecordPayment} className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">Record Payment</button>
          )}
          {onOpenFull && (
            <button onClick={onOpenFull} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50" title="Full view (print, PDF, attachments)">
              <span className="material-symbols-outlined text-base">open_in_full</span>
              Full view
            </button>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="text-xl font-bold text-gray-900">{invoice.invoice_number}</div>
          <div className="mt-1"><InvoiceStatusBadge status={invoice.status} size="sm" /></div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">Total</div>
          <div className="text-xl font-bold text-gray-900">{formatCurrency(invoice.total)}</div>
          {invoice.balance_due > 0 && (
            <div className="text-sm text-orange-600">Balance {formatCurrency(invoice.balance_due)}</div>
          )}
        </div>
      </div>

      {/* Bill-to + dates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Bill To</div>
          <div className="text-sm font-medium text-gray-900">{invoice.client_name}</div>
          {invoice.client_company && <div className="text-sm text-gray-500">{invoice.client_company}</div>}
          {invoice.client_email && <div className="text-sm text-gray-500">{invoice.client_email}</div>}
        </div>
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Invoice Date</span><span className="text-gray-900">{formatDate(invoice.invoice_date)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Due Date</span><span className={invoice.status === 'overdue' ? 'text-red-600' : 'text-gray-900'}>{formatDate(invoice.due_date)}</span></div>
          {invoice.job_title && <div className="flex justify-between"><span className="text-gray-500">Job</span><span className="text-gray-900">{invoice.job_title}</span></div>}
        </div>
      </div>

      {/* Line items */}
      <div className="rounded-lg border border-gray-200 overflow-hidden mb-5">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2 text-left">Description</th>
              <th className="px-4 py-2 text-right">Qty</th>
              <th className="px-4 py-2 text-right">Unit Price</th>
              <th className="px-4 py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((item, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="px-4 py-2 text-gray-800">{item.description}</td>
                <td className="px-4 py-2 text-right text-gray-600">{item.quantity}{item.unit ? ` ${item.unit}` : ''}</td>
                <td className="px-4 py-2 text-right text-gray-600">{formatCurrency(Number(item.unit_price))}</td>
                <td className="px-4 py-2 text-right font-medium text-gray-900">{formatCurrency(Number(item.quantity) * Number(item.unit_price))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-1.5 text-sm mb-5">
        <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{formatCurrency(invoice.subtotal)}</span></div>
        {invoice.gst_amount !== undefined && invoice.gst_amount > 0 ? (
          <>
            <div className="flex justify-between text-gray-600"><span>GST ({Number(invoice.gst_rate || 0) * 100}%)</span><span>{formatCurrency(invoice.gst_amount)}</span></div>
            <div className="flex justify-between text-gray-600"><span>PST ({Number(invoice.pst_rate || 0) * 100}%)</span><span>{formatCurrency(invoice.pst_amount || 0)}</span></div>
          </>
        ) : (
          <div className="flex justify-between text-gray-600"><span>Tax ({invoice.tax_rate}%)</span><span>{formatCurrency(invoice.tax_amount)}</span></div>
        )}
        <div className="flex justify-between font-semibold text-gray-900 border-t border-gray-200 pt-1.5"><span>Total</span><span>{formatCurrency(invoice.total)}</span></div>
        {invoice.amount_paid > 0 && (
          <div className="flex justify-between text-green-600"><span>Amount Paid</span><span>-{formatCurrency(invoice.amount_paid)}</span></div>
        )}
        <div className="flex justify-between font-bold text-orange-600 border-t border-gray-200 pt-1.5"><span>Balance Due</span><span>{formatCurrency(invoice.balance_due)}</span></div>
      </div>

      {/* Payment history */}
      {invoice.payments && invoice.payments.length > 0 && (
        <div className="mb-2">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Payment History</h4>
          <div className="space-y-2">
            {invoice.payments.map((p: InvoicePayment) => (
              <div key={p.id} className="flex justify-between items-center bg-white border border-gray-200 rounded-lg p-3">
                <div>
                  <div className="text-sm font-medium text-gray-900">{formatCurrency(p.amount)}</div>
                  <div className="text-xs text-gray-500">
                    {formatDate(p.payment_date)}{p.payment_method ? ` · ${p.payment_method}` : ''}{p.reference_number ? ` (${p.reference_number})` : ''}
                  </div>
                </div>
                <span className="material-symbols-outlined text-green-500">check_circle</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
